import { Typography, Tooltip, Tag, Dropdown } from "antd";
import { MoreOutlined } from "@ant-design/icons";
import { formatCurrency, formatDate } from "../../../helpers/helpers";
import { safeStringSort, safeNumberSort, safeDateSort } from "../../../helpers/sortHelpers";
import { BTN_TXT, STATUS } from "../../../constants/constants";
import { PERM_CODE } from "../../../constants/PermissionCodes";
import {
  canShowBoardResolutionAction,
  getKycVerificationModeTag,
  isManualKycRecord,
} from "../../../helpers/investorKyc";

const { Text } = Typography;

const PLACEHOLDER = "N/A";

const buildInvestorActionItems = ({
  record,
  status,
  privileges = [],
  onSendKycReminder,
  onDeactivate,
  onActivate,
  onView,
  onBRView,
  onReviewManualKyc,
}) => {
  const items = [];
  const hasPrivilege = (code) => privileges.includes(code);

  const rowStatus =
    record?.statusId ||
    (Array.isArray(status) ? (status.length === 1 ? status[0] : null) : status);

  if (
    rowStatus === STATUS.ACTIVE.ID ||
    rowStatus === STATUS.DEACTIVATED.ID ||
    rowStatus === STATUS.INACTIVE?.ID ||
    rowStatus === STATUS.PENDING.ID ||
    (!rowStatus && Array.isArray(status) && status.length > 0)
  ) {
    items.push({
      key: "view",
      label: BTN_TXT.VIEW,
      onClick: () => onView?.(record.orgId),
    });
  }

  if (rowStatus === STATUS.PENDING.ID && hasPrivilege(PERM_CODE.IVEDT)) {
    if (isManualKycRecord(record)) {
      items.push({
        key: "reviewManualKyc",
        label: BTN_TXT.REVIEW_MANUAL_KYC,
        onClick: () => onReviewManualKyc?.(record),
      });
    }

    if (canShowBoardResolutionAction(record)) {
      items.push({
        key: "viewBr",
        label: BTN_TXT.VIEW_BR,
        onClick: () => onBRView?.(record),
      });
    }

    items.push({
      key: "sendKycReminder",
      label: BTN_TXT.SEND_KYC_REMINDER,
      onClick: () => onSendKycReminder?.(record),
    });
  }

  if (rowStatus === STATUS.ACTIVE.ID && hasPrivilege(PERM_CODE.IASDE)) {
    items.push({
      key: "deactivate",
      label: BTN_TXT.DEACTIVATE,
      danger: true,
      onClick: () => onDeactivate?.(record),
    });
  }

  if (rowStatus === STATUS.DEACTIVATED.ID && hasPrivilege(PERM_CODE.IDSAC)) {
    items.push({
      key: "activate",
      label: BTN_TXT.ACTIVATE,
      onClick: () => onActivate?.(record),
    });
  }

  return items;
};

export const getInvestorColumns = ({
  onSendKycReminder,
  onDeactivate,
  onActivate,
  onView,
  status,
  privileges,
  onBRView,
  onReviewManualKyc,
}) => {
  const columns = [
    {
      title: "Investor Name",
      dataIndex: "investorName",
      key: "investorName",
      width: 200,
      sorter: (a, b) => safeStringSort(a?.investorName, b?.investorName),
      fixed: "left",
    },
    {
      title: "Investor Type",
      dataIndex: "investorTypeName",
      key: "investorTypeName",
      width: 150,
      sorter: (a, b) => safeStringSort(a?.investorTypeName, b?.investorTypeName),
      render: (text) => text || PLACEHOLDER,
    },
    {
      title: "Verification Mode",
      dataIndex: "kycVerificationMode",
      key: "kycVerificationMode",
      width: 150,
      sorter: (a, b) => safeStringSort(a?.kycVerificationMode, b?.kycVerificationMode),

      render: (mode, record) => {
        const { label, color } = getKycVerificationModeTag(mode, record);
        return <Tag color={color}>{label}</Tag>;
      },
    },
    {
      title: "Contact Email",
      dataIndex: "email",
      key: "email",
      width: 150,
      sorter: (a, b) => safeStringSort(a?.email, b?.email),

    },
    {
      title: "Available Balance",
      dataIndex: "availableBalance",
      key: "availableBalance",
      width: 180,
      sorter: (a, b) => safeNumberSort(a?.availableBalance, b?.availableBalance),

      render: (text) => formatCurrency(text || 0),
    },
    {
      title: "Active Investment Amount",
      dataIndex: "activeInvestmentAmt",
      key: "activeInvestmentAmt",
      width: 250,
      sorter: (a, b) => safeNumberSort(a?.activeInvestmentAmt, b?.activeInvestmentAmt),

      render: (text) => formatCurrency(text || 0),
    },
    {
      title: "Last Active Date",
      dataIndex: "lastActiveDate",
      key: "lastActiveDate",
      width: 180,
      sorter: (a, b) => safeDateSort(a?.lastActiveDate, b?.lastActiveDate),
      render: (text) => formatDate(text),
    },
    {
      title: "Comments",
      dataIndex: "remarks",
      key: "remarks",
      width: 150,
      sorter: (a, b) => safeStringSort(a?.remarks, b?.remarks),
      render: (text) =>
        text ? (
          <Tooltip title={text}>
            <Text ellipsis style={{ maxWidth: 120, display: "block" }}>
              {text}
            </Text>
          </Tooltip>
        ) : (
          PLACEHOLDER
        ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 80,
      fixed: "right",
      render: (_, record) => {
        const items = buildInvestorActionItems({
          record,
          status,
          privileges,
          onSendKycReminder,
          onDeactivate,
          onActivate,
          onView,
          onBRView,
          onReviewManualKyc,
        });

        if (!items.length) return null;

        return (
          <Dropdown
            menu={{ items }}
            trigger={["click"]}
            placement="bottomRight"
          >
            <MoreOutlined
              style={{ fontSize: 20, cursor: "pointer" }}
              aria-label={BTN_TXT.MORE}
            />
          </Dropdown>
        );
      },
    },
  ];

  return columns;
};
